import {
    ArrowLeft,
    Delete,
    FileDownload,
    Save,
    SendOutlined,
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    LinearProgress,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { H3 } from 'components/Typography';
import useBoolean from 'hooks/useBoolean';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone-uploader';
import { useNavigate, useParams } from 'react-router-dom';
import { default as axios } from 'utils/axios';
import downloadPreview from 'utils/downloadPreview';
import AbstractAuthor from './components/AbstractAuthor';
import Editor from './components/editor';
import LoadingScreen from 'components/LoadingScreen';

const EventAdminDetail = () => {
    const [data, setData] = useState(undefined);
    const [editorReadOnly, setEditorReadOnly] = useState(false);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [keywordInputValue, setKeywordInputValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [titleValue, setTitleValue] = useState('');
    const [fileFigureCaption, setFileFigureCaption] = useState(null);
    const [bodyText, setBodyText] = useState('');
    const [imageFigureURL, setImageFigureURL] = useState('');
    const [openFinalDialog, setOpenFinalDialog] = useState(false);
    const [figureDocTitle, setFigureDocTitle] = useState('');
    const [AuthorListKey, setAuthorListKey] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [figureError, setFigureError] = useState('');
    const [figureUploading, setFigureUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openFigureDialog, setOpenFigureDialog] = useState(false);
    const authorRef = useRef(null);
    const navigate = useNavigate();
    const [chipValue, setChipValue] = useState([]);
    const [authorData, setAuthorData] = useState([]);
    const { id } = useParams();
    const display = useTheme();
    const maxWord = 350;
    const maxKeywords = 5;
    const isMd = useMediaQuery(display.breakpoints.up('md'), {
        defaultMatches: true,
    });
    const MaxAuthor = 10;

    const showFigureUploadedBox = useBoolean(true);
    const loading = useBoolean();

    const getData = async () => {
        try {
            const response = await axios.get('api/prv/member_abstracts/' + id);

            setData(response.data);
            setTitleValue(response.data.title || '');

            if (response.data?.data?.figure?.caption) {
                setFileFigureCaption(response.data.data.figure.caption || null);
            }

            if (response.data?.data?.keyword?.length > 0) {
                var chips = [];
                for (var i = 0; i < response.data.data.keyword.length; i++) {
                    let keyword = response.data.data.keyword[i];
                    if (keyword && keyword !== '') {
                        chips.push(keyword);
                    }
                }
                setChipValue(chips);
            }

            if (
                response.data.status === 'submitted' ||
                response.data.status === 'approved'
            ) {
                localStorage.getItem('role') === 'admin'
                    ? setEditorReadOnly(false)
                    : setEditorReadOnly(true);
            }

            if (response.data?.data?.figure?.fileUrl) {
                showFigureUploadedBox.onFalse();
                setImageFigureURL(
                    `${process.env.REACT_APP_API_BASE_URL}/api/${response.data?.data?.figure?.fileUrl}`,
                );
                showFigureUploadedBox.onFalse();
                setFigureDocTitle(response.data.figure_file);
            }

            if (response.data.body) {
                setBodyText(response.data.body);
            } else {
                const structure = response.data.type.structures;
                structure.sort(function (a, b) {
                    var keyA = a.sequence,
                        keyB = b.sequence;
                    if (keyA < keyB) return -1;
                    if (keyA > keyB) return 1;
                    return 0;
                });
                var text = '';
                for (let i = 0; i < structure.length; i++) {
                    text +=
                        '<p class="ql-align-justify"><strong>' +
                        structure[i].name +
                        ': </strong></p><p class="ql-align-justify"><br></p>';
                }
                setBodyText(text);
            }

            setAuthorData(response.data.authors);

            return response.data;
        } catch (err) {
            console.log(err);
            return null;
        }
    };

    const getAuthorsData = async () => {
        await axios
            .get('api/submission-author/submission/' + id)
            .then((response) => {
                setAuthorData(response.data);
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        getData();
    }, []);

    const saveAbstract = async (status) => {
        setSaving(true);

        let values = {
            body: bodyText,
            title: titleValue,
            keyword: chipValue,
            caption: fileFigureCaption,
        };

        if (status) {
            values.status = status;
        }

        await axios
            .patch('/api/prv/member_abstracts/' + id, values)
            .then(() => {
                setTimeout(() => {
                    setSaving(false);
                }, 1000);
            })
            .catch((err) => {
                console.log('error on submission_save : ', err);
                setTimeout(() => {
                    setSaving(false);
                }, 1000);
            });
    };

    const authorlistChange = () => {
        getAuthorsData();
        setAuthorListKey((AuthorListKey) => AuthorListKey + 1);
    };

    const onEditorChangeBodyText = (text, delta, source, editor) => {
        setBodyText(text);
        var words = text
            .replace(/<\/?[^>]+(>|$)/g, ' ')
            .replace(/\r?\n|\r/g, ' ')
            .replace(/\s\s+/g, ' ')
            .trim()
            .split(' ');
        if (words.length > maxWord) {
            setSaveButtonDisabled(true);
        } else {
            setSaveButtonDisabled(false);
        }
    };

    const handleChips = (_, newValue) => {
        const next = (newValue || [])
            .map((x) => String(x).trim())
            .filter((x) => x);
        const deduped = Array.from(new Set(next));
        if (deduped.length > maxKeywords) return false;
        setChipValue(deduped);
    };

    const handleNoDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleYesDeleteDialog = async () => {
        setOpenDeleteDialog(false);
        await axios
            .delete('api/prv/member_abstracts/' + id)
            .then(() => {
                navigate('/dashboard/abstract-list');
            })
            .catch((err) => {
                console.log('error on submission_delete : ', err);
            });
    };

    const FigureUploader = ({
        submission_id,
        handleImage,
        disabled,
        ...other
    }) => {
        const getUploadParams = () => ({
            url:
                `${process.env.REACT_APP_API_BASE_URL}/api/prv/member_abstracts/upload/figure/` +
                submission_id,
            headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        const onStatusChange = (fileWithMeta, status) => {
            const { meta, xhr } = fileWithMeta || {};
            if (
                status === 'preparing' ||
                status === 'getting_upload_params' ||
                status === 'uploading'
            ) {
                setFigureUploading(true);
                setUploadProgress(
                    meta && typeof meta.progress === 'number'
                        ? meta.progress
                        : 0,
                );
                setFigureError('');
            }
            if (status === 'headers_received' || status === 'done') {
                try {
                    const resText = xhr && xhr.response;
                    const res = resText ? JSON.parse(resText) : null;
                    if (res && res.fileUrl) {
                        const fullUrl = `${process.env.REACT_APP_API_BASE_URL}/${res.fileUrl}`;
                        setImageFigureURL(fullUrl);
                        setFigureDocTitle(res.fileName || '');
                        setFigureError('');
                        setSnackbarMessage(
                            res.message || 'File uploaded successfully',
                        );
                        setSnackbarSeverity('success');
                        setSnackbarOpen(true);
                        if (typeof handleImage === 'function') handleImage();
                    } else {
                        setFigureError('Invalid server response');
                        setSnackbarMessage('Upload failed');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                    }
                } catch (e) {
                    setFigureError('Upload failed');
                    setSnackbarMessage('Upload failed');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                } finally {
                    setFigureUploading(false);
                    setUploadProgress(0);
                }
            }
            if (status === 'error_upload') {
                setFigureUploading(false);
                setUploadProgress(0);
                setFigureError('Upload failed');
                setSnackbarMessage('Upload failed');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
            if (status === 'error_file_size') {
                setFigureUploading(false);
                setUploadProgress(0);
                setFigureError('File too large (max 2MB)');
                setSnackbarMessage('File too large (max 2MB)');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
            if (status === 'error_file_type') {
                setFigureUploading(false);
                setUploadProgress(0);
                setFigureError('Only JPG/PNG files are allowed');
                setSnackbarMessage('Only JPG/PNG files are allowed');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
            if (status === 'removed') {
                setFigureUploading(false);
                setUploadProgress(0);
            }
        };

        const figureHandleSubmit = (files, allFiles) => {
            try {
                const first = files && files[0];
                const resText = first && first.xhr && first.xhr.response;
                const res = resText ? JSON.parse(resText) : null;
                if (res && res.fileUrl) {
                    const fullUrl = `${process.env.REACT_APP_API_BASE_URL}/${res.fileUrl}`;
                    setImageFigureURL(fullUrl);
                    setFigureDocTitle(res.fileName || '');
                    setFigureError('');
                    setSnackbarMessage(
                        res.message || 'File uploaded successfully',
                    );
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    if (typeof handleImage === 'function') handleImage();
                } else {
                    setFigureError('Invalid server response');
                    setSnackbarMessage('Upload failed');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            } catch (e) {
                setFigureError('Upload failed');
                setSnackbarMessage('Upload failed');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            } finally {
                setFigureUploading(false);
                setUploadProgress(0);
                allFiles && allFiles.forEach((f) => f.remove());
                getData();
            }
        };

        return (
            <Dropzone
                getUploadParams={getUploadParams}
                maxFiles={1}
                inputContent={(_, extra) =>
                    extra.reject
                        ? 'Only JPG/PNG files are allowed'
                        : 'Drop or click to upload (JPG/PNG, max 2MB)'
                }
                accept="image/jpeg, image/png"
                canCancel={false}
                maxSize={2097152}
                onChangeStatus={onStatusChange}
                onSubmit={figureHandleSubmit}
                disabled={disabled}
                styles={{
                    dropzone: { minHeight: 200, maxHeight: 250, padding: 0 },
                    inputLabel: { margin: 0 },
                    submitButton: { display: 'none' },
                    submitButtonContainer: { display: 'none' },
                    previewContainer: { display: 'none' },
                }}
            />
        );
    };

    const figureReUpload = () => {
        showFigureUploadedBox.onTrue();
        setImageFigureURL('');
        setFigureDocTitle('');
        setFigureError('');
        setSnackbarOpen(false);
        setUploadProgress(0);
    };

    const handleDownload = async () => {
        loading.onTrue();

        await saveAbstract();
        const latestData = await getData();
        await downloadPreview(latestData);

        loading.onFalse();
    };

    const confirmFinal = () => {
        saveAbstract();
        setOpenFinalDialog(true);
    };

    const confirmDelete = () => {
        setOpenDeleteDialog(true);
    };

    const handleNoFinalDialog = () => {
        setOpenFinalDialog(false);
    };

    const handleYesFinalDialog = async () => {
        setSubmitting(true);
        setOpenFinalDialog(false);

        if (!showFigureUploadedBox.value && !fileFigureCaption) {
            setSnackbarMessage('Please insert figure caption');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setSubmitting(false);

            return;
        }

        await saveAbstract('submitted').then(() => {
            navigate('/dashboard/abstract-list');
        });
    };

    const handleRemoveFigure = async () => {
        try {
            await axios.patch('/api/prv/member_abstracts/remove_figure/' + id);
            figureReUpload();
        } catch (err) {
            setSnackbarMessage('Failed to remove figure');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };
    return (
        <DashboardLayout>
            {loading.value && <LoadingScreen />}
            <Dialog
                open={openDeleteDialog}
                onClose={handleNoDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {'Delete Submission?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this submission?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNoDeleteDialog}>No</Button>
                    <Button onClick={handleYesDeleteDialog} autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openFinalDialog}
                onClose={handleNoFinalDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {'Submit Abstract?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to submit the abstract?
                        <Typography
                            marginTop={1}
                            fontStyle={'bold'}
                            color={'red'}
                        >
                            After submit you will not be able to change it.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNoFinalDialog}>No</Button>
                    <Button onClick={handleYesFinalDialog} autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            {data && (
                <Box pb={8}>
                    <Card>
                        <CardContent sx={{ p: 3 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 3 }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Box
                                        component="img"
                                        src="/logo-semarang.webp"
                                        alt="Semarang"
                                        sx={{ height: 40, borderRadius: 1 }}
                                    />
                                    <H3 sx={{ mt: 0 }}>Abstract Submission</H3>
                                </Stack>
                                <Chip label={data.type.name} color="warning" />
                            </Stack>
                            <Grid container spacing={3}>
                                <Grid item md={12} xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Box
                                            sx={{
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={600}
                                                color="text.primary"
                                            >
                                                Authors
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                disabled={
                                                    editorReadOnly ||
                                                    MaxAuthor <=
                                                        authorData.length
                                                }
                                                onClick={() =>
                                                    authorRef.current &&
                                                    authorRef.current.openAddAuthor()
                                                }
                                            >
                                                Add Author
                                            </Button>
                                        </Box>
                                        <AbstractAuthor
                                            key={'authorlist' + AuthorListKey}
                                            submission_id={id}
                                            disabledButton={
                                                editorReadOnly ||
                                                MaxAuthor <= authorData.length
                                            }
                                            disabledEdit={editorReadOnly}
                                            handleChange={authorlistChange}
                                            data={authorData}
                                            ref={authorRef}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            color="text.primary"
                                            sx={{ mb: 2 }}
                                        >
                                            Title
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            disabled={editorReadOnly}
                                            value={titleValue}
                                            label={
                                                titleValue
                                                    ? 'Title'
                                                    : 'Please input title'
                                            }
                                            onChange={(event) => {
                                                const { value } = event.target;
                                                setTitleValue(value);
                                            }}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled':
                                                    {
                                                        WebkitTextFillColor:
                                                            'black',
                                                        color: '#000',
                                                    },
                                                '& .MuiInputBase-input': {
                                                    fontFamily: 'Times',
                                                    fontWeight: 'bold',
                                                },
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            color="text.primary"
                                            sx={{ mb: 2 }}
                                        >
                                            Abstract
                                        </Typography>
                                        <Editor
                                            label={''}
                                            key={'aek-'}
                                            simple={false}
                                            id="editor-id"
                                            readOnly={editorReadOnly}
                                            value={bodyText}
                                            onChange={onEditorChangeBodyText}
                                            placeholder="Write the abstract here ..."
                                            maxWord={maxWord}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            color="text.primary"
                                            sx={{ mb: 2 }}
                                        >
                                            Keywords
                                        </Typography>
                                        <Autocomplete
                                            multiple
                                            id="tags-filled"
                                            disabled={editorReadOnly}
                                            options={[]}
                                            value={chipValue}
                                            inputValue={keywordInputValue}
                                            freeSolo
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        variant="outlined"
                                                        label={option}
                                                        {...getTagProps({
                                                            index,
                                                        })}
                                                    />
                                                ))
                                            }
                                            onChange={handleChips}
                                            onInputChange={(
                                                _,
                                                newInputValue,
                                            ) => {
                                                const options =
                                                    newInputValue.split(',');
                                                if (options.length > 1) {
                                                    if (
                                                        chipValue.length + 1 >
                                                        maxKeywords
                                                    )
                                                        return false;
                                                    if (
                                                        chipValue.indexOf(
                                                            options[0],
                                                        ) > -1
                                                    )
                                                        return false;
                                                    setChipValue(
                                                        chipValue
                                                            .concat(options)
                                                            .map((x) =>
                                                                x.trim(),
                                                            )
                                                            .filter((x) => x),
                                                    );
                                                    setKeywordInputValue('');
                                                } else {
                                                    setKeywordInputValue(
                                                        newInputValue,
                                                    );
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            mb: 1,
                                                            display: 'block',
                                                        }}
                                                    >
                                                        Press enter to insert,
                                                        backspace to delete
                                                    </Typography>
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        label="Keywords"
                                                        placeholder=""
                                                    />
                                                </>
                                            )}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Paper
                                        elevation={0}
                                        variant="outlined"
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Box
                                            display={
                                                showFigureUploadedBox.value
                                                    ? 'none'
                                                    : 'flex'
                                            }
                                            justifyContent={'center'}
                                            alignItems={'center'}
                                            flexDirection={'column'}
                                            padding={0}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={600}
                                                color="text.primary"
                                                sx={{ mb: 2 }}
                                            >
                                                Figure
                                            </Typography>
                                            <Grid
                                                container
                                                spacing={2}
                                                justifyContent="center"
                                            >
                                                <Grid item xs={12} md={7}>
                                                    <Card
                                                        sx={{
                                                            width: '100%',
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                        }}
                                                    >
                                                        <CardContent
                                                            sx={{ p: 1 }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                }}
                                                            >
                                                                <img
                                                                    alt="figure"
                                                                    src={
                                                                        imageFigureURL
                                                                    }
                                                                    onClick={() =>
                                                                        setOpenFigureDialog(
                                                                            true,
                                                                        )
                                                                    }
                                                                    style={{
                                                                        width: '100%',
                                                                        height: 'auto',
                                                                        maxHeight: 420,
                                                                        objectFit:
                                                                            'contain',
                                                                        borderRadius: 8,
                                                                        cursor: 'zoom-in',
                                                                    }}
                                                                />
                                                            </Box>
                                                        </CardContent>
                                                        <CardActions
                                                            sx={{
                                                                justifyContent:
                                                                    'center',
                                                                pt: 0,
                                                                flexWrap:
                                                                    'wrap',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={() =>
                                                                    setOpenFigureDialog(
                                                                        true,
                                                                    )
                                                                }
                                                            >
                                                                View
                                                            </Button>
                                                            <Button
                                                                disabled={
                                                                    editorReadOnly
                                                                }
                                                                onClick={
                                                                    figureReUpload
                                                                }
                                                                color="info"
                                                                variant="contained"
                                                            >
                                                                Replace
                                                            </Button>
                                                            <Button
                                                                disabled={
                                                                    editorReadOnly
                                                                }
                                                                onClick={
                                                                    handleRemoveFigure
                                                                }
                                                                color="primary"
                                                                variant="contained"
                                                            >
                                                                Remove figure
                                                            </Button>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                                <Grid item xs={12} md={5}>
                                                    <Paper
                                                        elevation={0}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {figureDocTitle ||
                                                                'Figure'}
                                                        </Typography>
                                                        <Box sx={{ mt: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                disabled={
                                                                    editorReadOnly
                                                                }
                                                                value={
                                                                    fileFigureCaption ||
                                                                    ''
                                                                }
                                                                label={
                                                                    'Figure Description'
                                                                }
                                                                inputProps={{
                                                                    maxLength: 255,
                                                                }}
                                                                autoComplete="off"
                                                                size="small"
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    const {
                                                                        value,
                                                                    } =
                                                                        event.target;
                                                                    setFileFigureCaption(
                                                                        value,
                                                                    );
                                                                }}
                                                                sx={{
                                                                    '& .MuiInputBase-input.Mui-disabled':
                                                                        {
                                                                            WebkitTextFillColor:
                                                                                'black',
                                                                            color: '#000',
                                                                        },
                                                                    '& .MuiInputBase-input':
                                                                        {
                                                                            fontFamily:
                                                                                'Times',
                                                                        },
                                                                }}
                                                            />
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box
                                            display={
                                                showFigureUploadedBox.value
                                                    ? 'block'
                                                    : 'none'
                                            }
                                            padding={0}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={600}
                                                color="text.primary"
                                                sx={{ mb: 2 }}
                                            >
                                                Figure
                                            </Typography>
                                            <Box marginBottom={1}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 1,
                                                        display: 'block',
                                                    }}
                                                >
                                                    JPG/PNG, max 2MB
                                                </Typography>
                                                <FigureUploader
                                                    submission_id={data.id}
                                                    handleImage={() =>
                                                        showFigureUploadedBox.onFalse()
                                                    }
                                                    disabled={
                                                        editorReadOnly ||
                                                        figureUploading
                                                    }
                                                />
                                            </Box>

                                            {figureUploading && (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={2}
                                                    sx={{ mb: 1 }}
                                                >
                                                    <LinearProgress
                                                        variant={
                                                            typeof uploadProgress ===
                                                            'number'
                                                                ? 'determinate'
                                                                : 'indeterminate'
                                                        }
                                                        value={uploadProgress}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Uploading...
                                                    </Typography>
                                                </Box>
                                            )}
                                            {figureError && (
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                >
                                                    {figureError}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Snackbar
                                            open={snackbarOpen}
                                            autoHideDuration={3000}
                                            onClose={() =>
                                                setSnackbarOpen(false)
                                            }
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'center',
                                            }}
                                        >
                                            <Alert
                                                severity={snackbarSeverity}
                                                onClose={() =>
                                                    setSnackbarOpen(false)
                                                }
                                                sx={{ width: '100%' }}
                                            >
                                                {snackbarMessage}
                                            </Alert>
                                        </Snackbar>
                                        <Dialog
                                            open={openFigureDialog}
                                            onClose={() =>
                                                setOpenFigureDialog(false)
                                            }
                                            maxWidth="md"
                                            fullWidth
                                        >
                                            <DialogContent sx={{ p: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <img
                                                        alt="figure"
                                                        src={imageFigureURL}
                                                        style={{
                                                            width: '100%',
                                                            height: 'auto',
                                                        }}
                                                    />
                                                </Box>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button
                                                    onClick={() =>
                                                        setOpenFigureDialog(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    Close
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>

                        <CardActions
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 3,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.level1',
                            }}
                        >
                            <Box
                                display={'flex'}
                                justifyContent={'space-between'}
                                gap={1}
                            >
                                <LoadingButton
                                    loading={loading.value}
                                    variant="contained"
                                    color="info"
                                    size="large"
                                    onClick={handleDownload}
                                    startIcon={isMd ? '' : <FileDownload />}
                                    endIcon={isMd ? <FileDownload /> : ''}
                                    loadingPosition="end"
                                    style={{ margin: 0 }}
                                >
                                    {isMd ? 'Preview' : ''}
                                </LoadingButton>
                                <LoadingButton
                                    loading={submitting}
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={confirmFinal}
                                    disabled={editorReadOnly}
                                    fullWidth
                                    startIcon={isMd ? '' : <SendOutlined />}
                                    endIcon={isMd ? <SendOutlined /> : ''}
                                    loadingPosition="end"
                                >
                                    {isMd ? 'Submit Abstract' : ''}
                                </LoadingButton>
                            </Box>
                            <Box display={'flex'} justifyContent={'flex-end'}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap={'wrap'}
                                >
                                    <Button
                                        variant="contained"
                                        color="info"
                                        size="large"
                                        startIcon={isMd ? '' : <ArrowLeft />}
                                        endIcon={isMd ? <ArrowLeft /> : ''}
                                        onClick={() => {
                                            navigate(
                                                '/dashboard/abstract-list',
                                            );
                                        }}
                                    >
                                        {isMd ? 'Back' : ''}
                                    </Button>
                                    <Button
                                        disabled={editorReadOnly}
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        onClick={confirmDelete}
                                        startIcon={isMd ? '' : <Delete />}
                                        endIcon={isMd ? <Delete /> : ''}
                                    >
                                        {isMd ? 'Delete' : ''}
                                    </Button>
                                    <LoadingButton
                                        loading={saving}
                                        loadingPosition="start"
                                        startIcon={isMd ? '' : <Save />}
                                        endIcon={isMd ? <Save /> : ''}
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                            editorReadOnly || saveButtonDisabled
                                        }
                                        size="large"
                                        onClick={() => saveAbstract()}
                                    >
                                        {isMd ? 'Save' : ''}
                                    </LoadingButton>
                                </Stack>
                            </Box>
                        </CardActions>
                    </Card>
                </Box>
            )}
        </DashboardLayout>
    );
};

export default EventAdminDetail;
